import { roleAccess, roleOf, type AgentCapability, type AgentRole } from "@cloudserver/contracts";
import { TriangleAlert } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

const CAPABILITIES = ["actions", "apps"] as const satisfies readonly AgentCapability[];

/**
 * The agent role as read access (monitoring only, or diagnostics) and the
 * two capabilities on top of it — Autopilot's actions and app management —
 * as independent toggles (docs/autopilot.md "Agent roles"; owner's decision
 * 2 of 2026-10-22). Each says what it puts on the server, and that it runs
 * as root. The value stays one role name (`roleOf`). Used on the create
 * page, in the enable dialog and in the role dialog.
 */
export function RoleOptions({
  legend,
  hint,
  value,
  onChange,
}: {
  legend: string;
  hint?: string;
  value: AgentRole;
  onChange: (role: AgentRole) => void;
}) {
  const { t } = useTranslation();
  const name = useId();
  const access = roleAccess(value);
  const levels = [
    {
      read: false,
      label: t("autopilot.role.levels.monitoring"),
      hint: t("autopilot.role.hints.monitoring"),
    },
    {
      read: true,
      label: t("autopilot.role.levels.read"),
      hint: t("autopilot.role.hints.agent-ro"),
    },
  ];
  return (
    <div className="flex flex-col gap-3">
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">{legend}</legend>
        {hint && <p className="-mt-1 text-sm text-muted">{hint}</p>}
        {levels.map((level) => {
          const id = `${name}-${level.read ? "read" : "monitoring"}`;
          return (
            <label
              key={id}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-sm hover:bg-surface-2 ${access.read === level.read ? "border-accent" : "border-line"}`}
            >
              <input
                type="radio"
                name={name}
                checked={access.read === level.read}
                onChange={() => onChange(roleOf({ ...access, read: level.read }))}
                aria-describedby={id}
                className="mt-0.5 size-4 shrink-0 accent-accent"
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium">{level.label}</span>
                <span id={id} className="text-muted">
                  {level.hint}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>
      <fieldset className="flex flex-col gap-2" disabled={!access.read}>
        <legend className="mb-1 text-sm font-medium">
          {t("autopilot.role.capabilities.legend")}
        </legend>
        <p className="-mt-1 text-sm text-muted">
          {access.read
            ? t("autopilot.role.capabilities.hint")
            : t("autopilot.role.capabilities.needsRead")}
        </p>
        {CAPABILITIES.map((capability) => {
          const id = `${name}-${capability}`;
          const on = access.read && access[capability];
          return (
            <label
              key={capability}
              className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm ${access.read ? "cursor-pointer hover:bg-surface-2" : "opacity-60"} ${on ? "border-accent" : "border-line"}`}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={!access.read}
                onChange={(event) =>
                  onChange(roleOf({ ...access, [capability]: event.target.checked }))
                }
                aria-describedby={id}
                className="mt-0.5 size-4 shrink-0 accent-accent"
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="font-medium">
                  {t(`autopilot.role.capabilities.${capability}`)}
                </span>
                <span id={id} className="text-muted">
                  {t(`autopilot.role.capabilityHints.${capability}`)}
                </span>
                {on && (
                  <span className="flex items-start gap-1.5 text-warn">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                    {t("autopilot.role.root")}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </fieldset>
    </div>
  );
}
